import type { ReadAccessTherapist, ReadAccessTherapistDTO } from "@akina/domain";
import { toCamelCase, toSnakeCase } from "@akina/utils";
import type {
  OrganizationImages,
  OrganizationImagesApi,
  OrganizationInfo,
  OrganizationInfoApi,
  ClinicTreatmentApi,
  ClinicTreatment,
} from "types/schemas";
import { readAccessTherapistMapper } from "../../mappers";
import { type ApiConfiguration } from "../shared";
import { ApiMethod } from "../shared";
import { uploadFileToS3 } from "../shared/uploadFileToS3";

export type InviteTherapistFormDTO = {
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

export type InviteTherapistForm = {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export const inviteTherapistMapper = {
  toDTO: (inviteTherapistForm: InviteTherapistForm) => ({
    first_name: inviteTherapistForm.firstName,
    last_name: inviteTherapistForm.lastName,
    email: inviteTherapistForm.email,
    role: inviteTherapistForm.role,
  }),
};

export type SupportContactResponseDTO = {
  email: string;
  telephone: string;
  use_whatsapp_chatbot: boolean;
}
export type SupportContactResponse = {
  email: string;
  telephone: string;
  useWhatsappChatbot: boolean;
}

export const supportContactMapper = {
  toDomain: (supportContactResponseDTO: SupportContactResponseDTO): SupportContactResponse => ({
    email: supportContactResponseDTO.email,
    telephone: supportContactResponseDTO.telephone,
    useWhatsappChatbot: supportContactResponseDTO.use_whatsapp_chatbot,
  }),
};

export const clinicApi = (config: ApiConfiguration) => ({
  async getById(id: string): Promise<OrganizationInfo | null> {
    const result = await config.httpService.fetch<OrganizationInfoApi | null>({
      method: ApiMethod.GET,
      url: config.endpoints.clinics.get(id),
    });

    if (!result) return null;

    return toCamelCase(result);
  },
  async update(clinic: OrganizationInfo): Promise<OrganizationInfo | null> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.clinics.update,
      data: toSnakeCase(clinic),
    });

    return await this.getById(clinic.id);
  },
  async getImages(id: string): Promise<OrganizationImages | null> {
    const result = await config.httpService.fetch<OrganizationImagesApi | null>({
      method: ApiMethod.GET,
      url: config.endpoints.clinics.getImages(id),
    });

    if (!result) return null;

    return toCamelCase(result);
  },

  async updateProfilePicture(newProfilePicture: File, clinicId: string): Promise<OrganizationInfo | null> {
    const result = await config.httpService.fetch<{ presigned_url: string }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.clinics.profilePicture.uploadUrl,
      data: { file_type: newProfilePicture.type },
    });

    await uploadFileToS3(result.presigned_url, newProfilePicture);

    return await this.getById(clinicId);
  },
  async deleteProfilePicture(clinicId: string): Promise<OrganizationInfo | null> {
    await config.httpService.fetch({
      method: ApiMethod.DELETE,
      url: config.endpoints.clinics.profilePicture.delete,
    });

    return await this.getById(clinicId);
  },
  async deleteImage(clinicId: string, imageUrl: string): Promise<OrganizationInfo | null> {
    await config.httpService.fetch({
      method: ApiMethod.DELETE,
      url: config.endpoints.clinics.images.delete(imageUrl),
    });

    return await this.getById(clinicId);
  },
  async addImage(clinicId: string, newImage: File): Promise<OrganizationInfo | null> {
    const result = await config.httpService.fetch<{ presigned_urls: string[] }>({
      method: ApiMethod.PATCH,
      url: config.endpoints.clinics.images.uploadUrl,
      data: { file_types: [newImage.type] },
    });

    if (result.presigned_urls.length) {
      await uploadFileToS3(result.presigned_urls[0], newImage);
    }

    return await this.getById(clinicId);
  },
  async inviteTherapist(invitationDetails: InviteTherapistForm): Promise<void> {
    await config.httpService.fetch<InviteTherapistFormDTO>({
      method: ApiMethod.POST,
      url: config.endpoints.clinics.inviteTherapist,
      data: inviteTherapistMapper.toDTO(invitationDetails),
    });
  },
  async getTherapists(clinicId: string, includeSchedulers?: boolean): Promise<ReadAccessTherapist[]> {
    let endpoint = config.endpoints.clinics.getTherapists(clinicId);
    if (includeSchedulers) endpoint += "?include_schedulers=true";
    const result = await config.httpService.fetch<ReadAccessTherapistDTO[]>({
      method: ApiMethod.GET,
      url: endpoint,
    });

    return result.map(readAccessTherapistMapper.toDomain);
  },
  async getTreatments(clinicId: string): Promise<ClinicTreatment[]> {
    const result = await config.httpService.fetch<ClinicTreatmentApi[]>({
      method: ApiMethod.GET,
      url: config.endpoints.clinics.getTreatments(clinicId),
    });

    return toCamelCase(result);
  },
  async getTherapyPlanKeys(clinicId: string): Promise<string[]> {
    const result = await config.httpService.fetch<{ therapy_plan_keys: string[] }>({
      method: ApiMethod.GET,
      url: config.endpoints.clinics.getTherapyPlanKeys(clinicId),
    });

    return result.therapy_plan_keys;
  },
  async getSupportContacts(clinicId?: string): Promise<SupportContactResponse> {
    const result = await config.httpService.fetch<SupportContactResponseDTO>({
      method: ApiMethod.GET,
      url: config.endpoints.clinics.getSupportContacts(clinicId),
    });

    return supportContactMapper.toDomain(result);
  },
});

export { useAddClinicImage } from "./useAddClinicImage";
export { useDeleteClinicImage } from "./useDeleteClinicImage";
export { useDeleteClinicProfilePicture } from "./useDeleteClinicProfilePicture";
export { useFetchClinic } from "./useFetchClinic";
export { useFetchClinicImages } from "./useFetchClinicImages";
export { useFetchSupportContacts } from "./useFetchSupportContacts";
export { useFetchTreatmentsForClinic } from "./useFetchTreatmentsForClinic";
export { useUpdateClinicProfilePicture } from "./useUpdateClinicProfilePicture";
export { useUpdateClinic } from "./useUpdateClinic";
